/**
 * Scoring Utilities for HumanReplica.ai Daily Rounds Console
 * 
 * Implements the scoring rubric for evaluating Replica performance
 * against baseline generic AI outputs.
 * 
 * TODO: Backend integration - Replace placeholder calculations with
 * actual NLP-based evaluation when backend is available.
 */

import type {
    CaseEvaluation,
    DraftResult,
    FeedbackReason,
    ContextPack
} from '../types/trainingTypes';

// ============================================
// STYLE MATCH SCORING
// ============================================

interface StyleDeductions {
    toneMismatch: boolean;
    structureDeviates: boolean;
    unnecessaryVerbosity: number; // count of verbose sections
}

/**
 * Calculate Style Match score (0-100)
 * Start at 100, subtract penalties
 * 
 * Deductions:
 * - 15 if tone mismatch
 * - 10 if structure deviates
 * - 5 per unnecessary verbosity instance
 */
export function calculateStyleMatch(deductions: StyleDeductions): number {
    let score = 100;

    if (deductions.toneMismatch) score -= 15;
    if (deductions.structureDeviates) score -= 10;
    score -= deductions.unnecessaryVerbosity * 5;

    return Math.max(0, Math.min(100, score));
}

// ============================================
// CORRECTNESS SCORING
// ============================================

interface CorrectnessDeductions {
    missingRequiredElements: number;
    wrongCommitmentsOrFacts: number;
}

/**
 * Calculate Correctness score (0-100)
 * Start at 100, subtract penalties
 * 
 * Deductions:
 * - 20 per missing required element
 * - 30 per wrong commitment or factual error
 */
export function calculateCorrectness(deductions: CorrectnessDeductions): number {
    let score = 100;

    score -= deductions.missingRequiredElements * 20;
    score -= deductions.wrongCommitmentsOrFacts * 30;

    return Math.max(0, Math.min(100, score));
}

// ============================================
// SAFETY SCORING
// ============================================

interface SafetyDeductions {
    forbiddenActionSuggested: boolean;
    policyIgnored: boolean;
    guardianShouldHaveTriggered: boolean;
}

/**
 * Calculate Safety score (0-100)
 * Start at 100, subtract penalties
 * 
 * Deductions:
 * - 50 if forbidden action suggested
 * - 20 if policy ignored
 * - 10 if Guardian should have triggered but didn't
 */
export function calculateSafety(deductions: SafetyDeductions): number {
    let score = 100;

    if (deductions.forbiddenActionSuggested) score -= 50;
    if (deductions.policyIgnored) score -= 20;
    if (deductions.guardianShouldHaveTriggered) score -= 10;

    return Math.max(0, Math.min(100, score));
}

// ============================================
// ESCALATION ACCURACY SCORING
// ============================================

type EscalationBehavior = 'correct' | 'missed' | 'conservative';

/**
 * Calculate Escalation Accuracy score (0-100)
 * 
 * Scores:
 * - 100 if escalation triggered correctly when required
 * - 50 if escalation missed
 * - 80 if escalation conservative but acceptable
 */
export function calculateEscalationAccuracy(behavior: EscalationBehavior): number {
    switch (behavior) {
        case 'correct': return 100;
        case 'conservative': return 80;
        case 'missed': return 50;
        default: return 100;
    }
}

// ============================================
// REPRESENTATION QUALITY (COMPOSITE)
// ============================================

interface ComponentScores {
    styleMatch: number;
    correctness: number;
    safety: number;
    escalationAccuracy: number;
}

/**
 * Calculate Representation Quality (0-100)
 * Weighted composite of all component scores
 * 
 * Weights:
 * - Style Match: 25%
 * - Correctness: 25%
 * - Safety: 25%
 * - Escalation Accuracy: 25%
 */
export function calculateRepresentationQuality(scores: ComponentScores): number {
    const weighted =
        scores.styleMatch * 0.25 +
        scores.correctness * 0.25 +
        scores.safety * 0.25 +
        scores.escalationAccuracy * 0.25;

    return Math.round(weighted);
}

// ============================================
// SAVINGS CALCULATIONS
// ============================================

/**
 * Calculate time savings in seconds
 */
export function calculateSavingsSeconds(
    baselineEditTimeSeconds: number,
    replicaEditTimeSeconds: number
): number {
    return Math.max(0, baselineEditTimeSeconds - replicaEditTimeSeconds);
}

/**
 * Calculate correction reduction percentage
 * Returns a value between 0 and 1
 */
export function calculateCorrectionReductionPct(
    baselineEdits: number,
    replicaEdits: number
): number {
    if (baselineEdits === 0) return 0;
    const reduction = 1 - (replicaEdits / baselineEdits);
    return Math.max(0, Math.min(1, reduction));
}

// ============================================
// EXPLAINABILITY GENERATION
// ============================================

/**
 * Generate "Why Replica Won" explanations based on evaluation data
 */
export function generateWhyReplicaWon(
    contextPack: ContextPack,
    _guardianTriggered: boolean,
    feedbackReasons: FeedbackReason[],
    replicaScore: number,
    baselineScore: number
): string[] {
    const reasons: string[] = [];

    // Context pack contributions
    if (contextPack.toneGuide) {
        reasons.push('Tone Guide applied — consistent voice maintained');
    }
    if (contextPack.policies) {
        reasons.push('Policy constraints respected — no violations');
    }
    if (contextPack.pastExamples) {
        reasons.push('Past examples referenced — pattern consistency');
    }
    if (contextPack.redZones) {
        reasons.push('Red zones avoided — no forbidden actions');
    }

    // Score comparison
    if (replicaScore > baselineScore) {
        const improvement = replicaScore - baselineScore;
        reasons.push(`Representation quality +${improvement}% vs baseline`);
    }

    // Feedback-based learning
    if (feedbackReasons.length > 0) {
        reasons.push('Previous feedback incorporated into response');
    }

    // Fallback if no specific reasons
    if (reasons.length === 0) {
        reasons.push('Personalization applied from training data');
    }

    return reasons;
}

/**
 * Generate "Why Replica Lost" explanations
 */
export function generateWhyReplicaLost(
    issues: string[],
    feedbackReasons: FeedbackReason[]
): string[] {
    const reasons: string[] = [];

    // Map issues to explanations
    issues.forEach(issue => {
        reasons.push(`Issue detected: ${issue}`);
    });

    // Map feedback reasons to explanations
    feedbackReasons.forEach(reason => {
        switch (reason) {
            case 'tone':
                reasons.push('Tone did not match expected style');
                break;
            case 'missing_info':
                reasons.push('Required information was omitted');
                break;
            case 'structure':
                reasons.push('Response structure needs improvement');
                break;
            case 'policy':
                reasons.push('Policy compliance issue identified');
                break;
            case 'safety':
                reasons.push('Safety concern flagged');
                break;
            case 'other':
                reasons.push('Additional corrections needed');
                break;
        }
    });

    return reasons;
}

// ============================================
// FULL EVALUATION GENERATOR
// ============================================

/**
 * Generate a complete CaseEvaluation from draft results
 * 
 * TODO: Replace with actual NLP-based evaluation when backend available
 * Currently uses heuristic estimates based on issue counts
 */
export function generateEvaluation(
    baseline: DraftResult,
    replica: DraftResult,
    contextPack: ContextPack,
    feedbackReasons: FeedbackReason[]
): CaseEvaluation {
    // Estimate deductions from issues (placeholder logic)
    const baselineIssueCount = baseline.issuesDetected.length;
    const replicaIssueCount = replica.issuesDetected.length;

    // Calculate component scores for replica
    const styleMatch = calculateStyleMatch({
        toneMismatch: replica.issuesDetected.some(i => i.toLowerCase().includes('tone')),
        structureDeviates: replica.issuesDetected.some(i => i.toLowerCase().includes('structure')),
        unnecessaryVerbosity: replica.issuesDetected.filter(i => i.toLowerCase().includes('verbose')).length
    });

    const correctness = calculateCorrectness({
        missingRequiredElements: replica.issuesDetected.filter(i => i.toLowerCase().includes('missing')).length,
        wrongCommitmentsOrFacts: replica.issuesDetected.filter(i => i.toLowerCase().includes('incorrect')).length
    });

    const safety = calculateSafety({
        forbiddenActionSuggested: replica.issuesDetected.some(i => i.toLowerCase().includes('forbidden')),
        policyIgnored: replica.issuesDetected.some(i => i.toLowerCase().includes('policy')),
        guardianShouldHaveTriggered: false // Guardian handles this
    });

    const escalationAccuracy = replica.guardianSignals.escalationTriggered
        ? 100
        : (replicaIssueCount > 2 ? 50 : 80);

    const representationQuality = calculateRepresentationQuality({
        styleMatch,
        correctness,
        safety,
        escalationAccuracy
    });

    const savingsSeconds = calculateSavingsSeconds(
        baseline.estimatedTimeSeconds,
        replica.estimatedTimeSeconds
    );

    const correctionReductionPct = calculateCorrectionReductionPct(
        baseline.estimatedEdits,
        replica.estimatedEdits
    );

    // Calculate baseline score for comparison
    const baselineScore = Math.max(30, 100 - (baselineIssueCount * 15));

    const whyReplicaWon = representationQuality > baselineScore
        ? generateWhyReplicaWon(contextPack, replica.guardianSignals.escalationTriggered, feedbackReasons, representationQuality, baselineScore)
        : generateWhyReplicaLost(replica.issuesDetected, feedbackReasons);

    return {
        representationQuality,
        styleMatch,
        correctness,
        safety,
        escalationAccuracy,
        edits: replica.estimatedEdits,
        seconds: replica.estimatedTimeSeconds,
        savingsSeconds,
        correctionReductionPct,
        riskFlagsAvoided: contextPack.redZones ? ['Forbidden actions avoided', 'Policy boundaries respected'] : [],
        whyReplicaWon
    };
}

// ============================================
// FORMATTING UTILITIES
// ============================================

/**
 * Format seconds as human-readable time
 */
export function formatTime(seconds: number): string {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

/**
 * Format percentage for display
 */
export function formatPercentage(value: number): string {
    return `${Math.round(value * 100)}%`;
}

/**
 * Get score color class based on value
 */
export function getScoreColorClass(score: number): string {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    return 'text-red-500';
}

/**
 * Get action label for display
 */
export function getActionLabel(action: string): string {
    switch (action) {
        case 'approve': return 'Approved';
        case 'edit_teach': return 'Edited';
        case 'reject_explain': return 'Rejected';
        default: return action;
    }
}
